import { CommonActions } from '@bugbug/core/actions/actions';
import { useCallback } from 'react';

import type { Group } from '@bugbug/core/types/groups';
import type { PlaybackCursorPosition } from '@bugbug/core/types/playback';
import type { Step } from '@bugbug/core/types/steps';
import { selectTestRunIdFromUrl } from '~/modules/misc.selectors';
import { useAppDispatch, useAppSelector } from '~/modules/store';
import { selectPlaybackCursorPosition } from '~/modules/uiState/uiState.selectors';

import {
  selectCurrentTest,
  selectCurrentTestGroups,
  selectCurrentTestIsRecording,
  selectSortedSteps,
} from '../../tests.selectors';
import { useStopTest } from '../useStopTest/useStopTest';
import { useStopTestRecording } from '../useStopTestRecording/useStopTestRecording';

export const useCleanupAfterTestPartialsDelete = () => {
  const sortedSteps = useAppSelector(selectSortedSteps);
  const testRunId = useAppSelector(selectTestRunIdFromUrl);
  const isRecording = useAppSelector(selectCurrentTestIsRecording);
  const currentTest = useAppSelector(selectCurrentTest);
  const currentTestGroups = useAppSelector(selectCurrentTestGroups);
  const cursorPosition = useAppSelector(selectPlaybackCursorPosition);
  const [stopRecording] = useStopTestRecording();
  const [stopRunning] = useStopTest();
  const dispatch = useAppDispatch();

  const findNewAfterStepUsingDataBeforeRemoval = useCallback(
    ({ removedStepsIds, removedGroupsIds }) => {
      if (!currentTest || !cursorPosition?.afterStep) return null;

      if (
        !cursorPosition?.afterStep?.id &&
        !removedGroupsIds.includes(cursorPosition.afterStep.groupId)
      ) {
        return {
          id: null,
          groupId: cursorPosition.afterStep.groupId,
        };
      }

      const groupIndex = currentTest.groups.indexOf(cursorPosition.afterStep.groupId);

      const afterStep = cursorPosition.afterStep.id
        ? cursorPosition.afterStep
        : sortedSteps.findLast((s) => currentTest.groups.indexOf(s.groupId) < groupIndex);

      const cursorStepIndex = sortedSteps.findIndex(
        (step) => step.id === afterStep?.id && step.groupId === afterStep?.groupId,
      );

      if (cursorStepIndex === -1) {
        return null;
      }

      let position: PlaybackCursorPosition['afterStep'] = null;
      let searchIndex = cursorStepIndex;
      while (searchIndex >= 0) {
        const step = sortedSteps[searchIndex];
        if (!removedStepsIds.includes(step.id) && !removedGroupsIds.includes(step.groupId)) {
          position = {
            id: step.id,
            groupId: step.groupId,
          };
          break;
        }
        if (
          currentTestGroups[step.groupId]?.steps.indexOf(step.id) === 0 &&
          !removedGroupsIds.includes(step.groupId)
        ) {
          position = {
            id: null,
            groupId: step.groupId,
          };
          break;
        }
        searchIndex -= 1;
      }

      return position;
    },
    [cursorPosition?.afterStep, sortedSteps, currentTest, currentTestGroups],
  );

  const stopRunnerIfRemovedAllSteps = useCallback(
    async ({ removedStepsIds, removedGroupsIds }) => {
      const filteredSteps = sortedSteps.filter(
        (step) => !removedStepsIds.includes(step.id) && !removedGroupsIds.includes(step.groupId),
      );

      if (filteredSteps.length === 0 && testRunId && currentTest) {
        if (isRecording) {
          await stopRecording({
            testId: currentTest.id,
            testRunId,
          });
        } else {
          await stopRunning({
            testId: currentTest.id,
            params: { testRunId },
          });
        }
      }
    },
    [sortedSteps, testRunId, currentTest, isRecording, stopRecording, stopRunning],
  );

  return useCallback(
    ({ stepsIds, groupsIds }: { stepsIds: Step['id'][]; groupsIds: Group['id'][] }) => {
      const afterStep = findNewAfterStepUsingDataBeforeRemoval({
        removedStepsIds: stepsIds,
        removedGroupsIds: groupsIds,
      });
      dispatch(CommonActions.refreshPlaybackCursorPosition({ step: afterStep }));
      stopRunnerIfRemovedAllSteps({
        removedStepsIds: stepsIds,
        removedGroupsIds: groupsIds,
      });
    },
    [dispatch, findNewAfterStepUsingDataBeforeRemoval, stopRunnerIfRemovedAllSteps],
  );
};
